import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Truck, RefreshCw } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'CONFIRMED', 'DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'];
const LIMIT = 20;

export const DispatchShipments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [shipments, setShipments] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [companyFilter, setCompanyFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.listCompanies(200, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (search) filters.trackingNumber = search;
      if (companyFilter) filters.companyId = companyFilter;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, search, statusFilter, companyFilter]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id: string) => { await apiClient.confirmShipment(id); load(); };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          All Shipments <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400 }}>({total.toLocaleString()})</span>
        </h1>
        <button className="btn btn-ghost" onClick={load} disabled={loading}><RefreshCw size={14} /></button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking number..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={() => { setSearch(''); setStatusFilter(''); setCompanyFilter(''); setPage(0); }}>Clear</button>
        </div>

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state"><h3>No shipments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Tracking #</th>
                    <th>Company</th>
                    <th>Sender</th>
                    <th>Receiver</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => (
                    <tr key={s.id}>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{s.company?.name ?? '—'}</td>
                      <td>{s.senderName}</td>
                      <td>{s.receiverName}</td>
                      <td>{s.receiverCity || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} RWF</td>
                      <td style={{ fontSize: 12 }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/dispatch/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        {s.status === 'PENDING' && (
                          <button className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => confirm(s.id)}>Confirm</button>
                        )}
                        {s.status === 'CONFIRMED' && (
                          <Link to={`/dispatch/shipments/${s.id}/dispatch`} className="btn btn-primary" style={{ padding: '3px 8px', fontSize: 12 }}>
                            <Truck size={12} /> Dispatch
                          </Link>
                        )}
                        {['DEPARTED', 'IN_TRANSIT'].includes(s.status) && (
                          <Link to={`/dispatch/shipments/${s.id}/deliver`} className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }}>Deliver</Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
